import { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash, ListBullets, Users, Wrench, Truck, ReceiptX, Sparkle } from "@phosphor-icons/react";
import { formatCurrencyExact } from "../lib/romEngine";
import { ARITH_RE, evalArithmetic } from "../lib/formula";
import {
    computeTM, labourLineSubtotal, equipmentLineSubtotal, subLineSubtotal,
    miscLineSubtotal, buildEmptyLabourFromRateCard, staffingSummary,
} from "../lib/tmEngine";

/* ---------------- Cost Tracker (sticky right side) ---------------- */
export function CostTracker({ tm, onOpen, onShowStaffing }) {
    const totals = useMemo(() => computeTM(tm), [tm]);
    const items = [
        { key: "labour",      label: "A: Labour",       v: totals.labour,      icon: Users,      color: "var(--rom-navy)" },
        { key: "consumables", label: "B: Consumables",  v: totals.consumables, icon: ListBullets, color: "#94a3b8", suffix: `${(tm.consumablesPct * 100).toFixed(0)}% of A` },
        { key: "equipment",   label: "C: Equipment",    v: totals.equipment,   icon: Wrench,     color: "var(--rom-orange)" },
        { key: "subs",        label: "D: Subcontractors", v: totals.subs,      icon: Truck,      color: "var(--rom-green)", suffix: "+20% markup" },
        { key: "misc",        label: "E: Miscellaneous", v: totals.misc,       icon: ReceiptX,   color: "#a855f7", suffix: "+10/10%" },
    ];
    const filledCount = [totals.labour, totals.equipment, totals.subs, totals.misc].filter((v) => v > 0).length;

    return (
        <div className="rom-card border-t-4 border-t-[var(--rom-orange)]" data-testid="tm-cost-tracker">
            <div className="px-5 pt-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-2)] font-semibold">
                    Estimated Job Total
                </div>
                <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[var(--rom-navy)] mt-1"
                     data-testid="tm-grand-total">
                    {formatCurrencyExact(totals.grandTotal)}
                </div>
                <div className="text-[11px] text-[var(--rom-text-3)] mt-1">
                    Time &amp; Material — line-item entry
                </div>
            </div>
            <div className="px-3 pt-4 pb-3 space-y-1.5">
                {items.map((it) => {
                    const Icon = it.icon;
                    const clickable = it.key !== "consumables";
                    return (
                        <button
                            key={it.key}
                            onClick={clickable ? () => onOpen(it.key) : () => onOpen("consumables")}
                            data-testid={`tracker-${it.key}`}
                            className="w-full text-left px-3 py-3 rounded-[4px] border border-[var(--rom-border)] hover:border-[var(--rom-orange)] hover:bg-[var(--rom-orange-soft)]/50 transition-colors flex items-center gap-3 group"
                        >
                            <span className="w-7 h-7 rounded-[3px] flex items-center justify-center shrink-0"
                                  style={{ background: it.color }}>
                                <Icon size={14} weight="bold" className="text-white" />
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-[var(--rom-text)]">{it.label}</div>
                                {it.suffix && <div className="text-[10px] text-[var(--rom-text-3)] uppercase tracking-wider">{it.suffix}</div>}
                            </div>
                            <div className="font-mono tabular text-sm font-bold text-[var(--rom-text)]">
                                {formatCurrencyExact(it.v)}
                            </div>
                            <span className="text-[var(--rom-text-3)] group-hover:text-[var(--rom-orange)] text-xs">›</span>
                        </button>
                    );
                })}
            </div>
            <div className="border-t border-[var(--rom-border)] px-4 py-3 flex items-center justify-between bg-slate-50 rounded-b-[6px]">
                <div className="text-[11px] text-[var(--rom-text-2)]">
                    {filledCount}/4 sections filled
                </div>
                <button
                    onClick={onShowStaffing}
                    disabled={totals.labour === 0}
                    className="btn btn-primary btn-sm"
                    data-testid="show-staffing-btn"
                >
                    <Sparkle size={12} weight="fill" /> Labour required
                </button>
            </div>
        </div>
    );
}

/* ---------------- Generic Modal shell ---------------- */
export function Modal({ open, title, subtitle, onClose, children, footer, wide = false }) {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", handler);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="tm-modal-backdrop"
        >
            <div
                className={`bg-white rounded-[6px] shadow-2xl my-8 w-full ${wide ? "max-w-6xl" : "max-w-4xl"}`}
                onClick={(e) => e.stopPropagation()}
                data-testid="tm-modal-panel"
            >
                <div className="px-6 py-4 border-b border-[var(--rom-border)] flex items-start justify-between sticky top-0 bg-white z-10 rounded-t-[6px]">
                    <div>
                        <h2 className="font-display font-black text-2xl">{title}</h2>
                        {subtitle && <div className="text-xs text-[var(--rom-text-2)] mt-0.5">{subtitle}</div>}
                    </div>
                    <button onClick={onClose} className="p-2 -mr-2 hover:bg-slate-100 rounded" data-testid="tm-modal-close">
                        <X size={18} weight="bold" />
                    </button>
                </div>
                <div className="px-6 py-5">{children}</div>
                {footer && (
                    <div className="px-6 py-4 border-t border-[var(--rom-border)] bg-slate-50 rounded-b-[6px] flex items-center justify-between gap-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------------- Number input (cell-style, Excel-formula capable) ----------------
 * Plain numbers update live; expressions like (5*30)+12 or =4*8 evaluate on
 * Enter/blur (orange border + "= result" tooltip while typing a formula). */
function NumCell({ value, onChange, step = "1", placeholder = "0", testId, min }) {
    const [text, setText] = useState(value === undefined || value === null || Number.isNaN(value) ? "" : String(value));
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        if (!focused) setText(value === undefined || value === null || Number.isNaN(value) ? "" : String(value));
    }, [value, focused]);

    const isFormula = ARITH_RE.test(text);
    const preview = isFormula ? evalArithmetic(text) : null;

    function handleChange(v) {
        setText(v);
        if (!ARITH_RE.test(v)) onChange(v === "" ? 0 : Number(v));
    }
    function commit() {
        if (ARITH_RE.test(text)) {
            const r = evalArithmetic(text);
            if (r.ok) {
                setText(r.value === "" ? "" : String(r.value));
                onChange(r.value === "" ? 0 : Number(r.value));
            }
        }
        setFocused(false);
    }
    return (
        <input
            type="text" inputMode="decimal"
            min={min}
            className={`w-full bg-transparent text-right font-mono text-sm border focus:bg-white focus:outline-none rounded px-2 py-1 ${isFormula ? "border-[var(--rom-orange)]" : "border-[var(--rom-border)] hover:border-[var(--rom-border-strong)] focus:border-[var(--rom-orange)]"}`}
            value={text}
            placeholder={placeholder}
            title={preview && preview.ok && preview.value !== "" ? `= ${preview.value}` : (preview && !preview.ok ? preview.err : undefined)}
            onFocus={() => setFocused(true)}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
            data-testid={testId}
        />
    );
}

/* ---------------- A: Labour Modal ---------------- */
export function LabourModal({ open, onClose, scheduleA, usePrime, lines, onSave }) {
    const [draft, setDraft] = useState(lines);
    useEffect(() => {
        if (open) {
            // Seed from rate schedule if empty
            if (!lines || lines.length === 0) {
                setDraft(buildEmptyLabourFromRateCard(scheduleA, usePrime));
            } else {
                setDraft(lines);
            }
        }
    }, [open, lines, scheduleA, usePrime]);

    const setRow = (i, key, val) => {
        setDraft((d) => {
            const next = [...d];
            next[i] = { ...next[i], [key]: val };
            // If billing rate edited, auto-recalc OT rate at 1.5×
            if (key === "billingRate") {
                next[i].otRate = Number(val) * 1.5;
            }
            return next;
        });
    };
    const addCustom = () => setDraft((d) => [...d, {
        role: "Custom role", qty: 1, billingRate: 0, hours: 8, otRate: 0, otHours: 0, days: 1,
    }]);
    const delRow = (i) => setDraft((d) => d.filter((_, idx) => idx !== i));

    const total = draft.reduce((s, l) => s + labourLineSubtotal(l), 0);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Schedule A — Labour"
            subtitle="Set crew / hours / days per role. OT rate auto-fills at 1.5× billing rate."
            wide
            footer={
                <>
                    <button onClick={addCustom} className="btn btn-ghost" data-testid="labour-add-custom">
                        <Plus size={14} weight="bold" /> Add custom role
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-[var(--rom-text-2)]">
                            Schedule A subtotal: <span className="font-mono font-bold text-[var(--rom-navy)]" data-testid="labour-modal-subtotal">{formatCurrencyExact(total)}</span>
                        </div>
                        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                        <button
                            onClick={() => { onSave(draft); onClose(); }}
                            className="btn btn-primary"
                            data-testid="labour-modal-save"
                        >
                            Save Labour
                        </button>
                    </div>
                </>
            }
        >
            <table className="rom-table">
                <thead>
                    <tr>
                        <th>Role</th>
                        <th className="numeric">QTY</th>
                        <th className="numeric">Billing $/hr</th>
                        <th className="numeric">Reg Hrs/day</th>
                        <th className="numeric">OT $/hr</th>
                        <th className="numeric">OT Hrs/day</th>
                        <th className="numeric">Days</th>
                        <th className="numeric">Subtotal</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {draft.map((row, i) => (
                        <tr key={i} data-testid={`labour-modal-row-${i}`}>
                            <td className="font-medium">
                                <input
                                    className="w-full bg-transparent border border-transparent hover:border-[var(--rom-border)] focus:border-[var(--rom-orange)] focus:outline-none rounded px-2 py-1"
                                    value={row.role || ""}
                                    onChange={(e) => setRow(i, "role", e.target.value)}
                                    data-testid={`labour-modal-role-${i}`}
                                />
                            </td>
                            <td className="numeric"><NumCell value={row.qty} onChange={(v) => setRow(i, "qty", v)} testId={`labour-modal-qty-${i}`} /></td>
                            <td className="numeric"><NumCell step="0.01" value={row.billingRate} onChange={(v) => setRow(i, "billingRate", v)} testId={`labour-modal-bill-${i}`} /></td>
                            <td className="numeric"><NumCell step="0.5" value={row.hours} onChange={(v) => setRow(i, "hours", v)} testId={`labour-modal-hrs-${i}`} /></td>
                            <td className="numeric"><NumCell step="0.01" value={row.otRate} onChange={(v) => setRow(i, "otRate", v)} testId={`labour-modal-otrate-${i}`} /></td>
                            <td className="numeric"><NumCell step="0.5" value={row.otHours} onChange={(v) => setRow(i, "otHours", v)} testId={`labour-modal-othrs-${i}`} /></td>
                            <td className="numeric"><NumCell step="0.5" value={row.days} onChange={(v) => setRow(i, "days", v)} testId={`labour-modal-days-${i}`} /></td>
                            <td className="numeric font-mono font-semibold tabular">{formatCurrencyExact(labourLineSubtotal(row))}</td>
                            <td className="text-right">
                                <button onClick={() => delRow(i)} className="text-[var(--rom-danger)] hover:opacity-70 p-1" data-testid={`labour-modal-del-${i}`}>
                                    <Trash size={14} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Modal>
    );
}

/* ---------------- B: Consumables Modal ---------------- */
export function ConsumablesModal({ open, onClose, labourTotal, pct, onSave }) {
    const [draft, setDraft] = useState(pct);
    useEffect(() => { if (open) setDraft(pct); }, [open, pct]);
    const options = [0.05, 0.10, 0.15, 0.20];
    const computed = labourTotal * draft;
    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Schedule B — Consumables"
            subtitle="Percentage of Schedule A (Labour) cost"
            footer={
                <>
                    <span />
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-[var(--rom-text-2)]">
                            Consumables: <span className="font-mono font-bold text-[var(--rom-navy)]" data-testid="consumables-modal-value">{formatCurrencyExact(computed)}</span>
                        </div>
                        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                        <button onClick={() => { onSave(draft); onClose(); }} className="btn btn-primary" data-testid="consumables-modal-save">Save</button>
                    </div>
                </>
            }
        >
            <div className="space-y-4">
                <div className="text-sm text-[var(--rom-text-2)]">
                    Consumables (PPE, cleaning solutions, plastic, bags, tape, etc.) are billed as a percentage of the Schedule A labour total. Choose the applicable rate:
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    {options.map((o) => (
                        <button
                            key={o}
                            onClick={() => setDraft(o)}
                            data-testid={`consumables-pct-${o}`}
                            className={`px-5 py-3 text-sm font-semibold rounded-[4px] border transition-colors ${
                                draft === o
                                    ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]"
                                    : "bg-white text-[var(--rom-text)] border-[var(--rom-border-strong)] hover:border-[var(--rom-navy)]"
                            }`}
                        >
                            {(o * 100).toFixed(0)}%
                        </button>
                    ))}
                    <div className="flex items-center gap-1.5 ml-1 pl-3 border-l border-[var(--rom-border)]">
                        <span className="text-xs text-[var(--rom-text-3)] uppercase tracking-wider font-semibold">Custom</span>
                        <input
                            type="number" step="0.5"
                            className="w-24 h-10 px-2 text-right font-mono text-sm rounded-[4px] border border-[var(--rom-border-strong)] focus:border-[var(--rom-orange)] focus:outline-none"
                            value={Number((draft * 100).toFixed(2))}
                            onChange={(e) => setDraft(e.target.value === "" ? 0 : Number(e.target.value) / 100)}
                            placeholder="e.g. 7 or -5"
                            data-testid="consumables-pct-custom"
                        />
                        <span className="text-sm text-[var(--rom-text-2)]">%</span>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="p-3 rounded bg-slate-50 border border-[var(--rom-border)]">
                        <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)]">Schedule A (Labour)</div>
                        <div className="font-mono font-bold text-lg">{formatCurrencyExact(labourTotal)}</div>
                    </div>
                    <div className="p-3 rounded bg-[var(--rom-orange-soft)] border border-[var(--rom-orange)]">
                        <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)]">B Consumables ({(draft * 100).toFixed(0)}%)</div>
                        <div className="font-mono font-bold text-lg text-[var(--rom-orange-dark)]">{formatCurrencyExact(computed)}</div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

/* ---------------- C: Equipment Modal ---------------- */
export function EquipmentModal({ open, onClose, scheduleC, lines, onSave }) {
    const [draft, setDraft] = useState(lines);
    const [search, setSearch] = useState("");
    useEffect(() => { if (open) { setDraft(lines || []); setSearch(""); } }, [open, lines]);

    // Build a quick lookup of catalog
    const byCategory = useMemo(() => {
        const map = new Map();
        (scheduleC || []).forEach((row) => {
            const cat = row.category || "Other";
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(row);
        });
        return map;
    }, [scheduleC]);

    const draftByItem = useMemo(() => {
        const map = new Map();
        draft.forEach((l, idx) => map.set(l.item, { ...l, idx }));
        return map;
    }, [draft]);

    const upsert = (item, dailyRate, key, val) => {
        setDraft((d) => {
            const existing = d.findIndex((l) => l.item === item);
            if (existing >= 0) {
                const next = [...d];
                next[existing] = { ...next[existing], [key]: val };
                // Auto-remove row if qty and days both zero
                if (n(next[existing].qty) === 0 && n(next[existing].days) === 0) {
                    next.splice(existing, 1);
                }
                return next;
            }
            return [...d, { item, dailyRate, qty: 0, days: 0, [key]: val }];
        });
    };

    const totalEq = draft.reduce((s, l) => s + equipmentLineSubtotal(l), 0);

    const filteredCategories = Array.from(byCategory.entries()).map(([cat, rows]) => [
        cat,
        rows.filter((r) => !search || r.item.toLowerCase().includes(search.toLowerCase())),
    ]).filter(([, rows]) => rows.length > 0);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Schedule C — Equipment"
            subtitle="Enter Qty + Days per item. Use the search to find equipment quickly."
            wide
            footer={
                <>
                    <input
                        type="text"
                        placeholder="Search equipment…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input max-w-xs"
                        data-testid="equipment-search"
                    />
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-[var(--rom-text-2)]">
                            Equipment subtotal: <span className="font-mono font-bold text-[var(--rom-navy)]" data-testid="equipment-modal-subtotal">{formatCurrencyExact(totalEq)}</span>
                        </div>
                        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                        <button onClick={() => { onSave(draft); onClose(); }} className="btn btn-primary" data-testid="equipment-modal-save">Save Equipment</button>
                    </div>
                </>
            }
        >
            <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-5">
                {filteredCategories.map(([cat, rows]) => (
                    <div key={cat}>
                        <h4 className="font-display font-bold text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-2)] mb-2 sticky top-0 bg-white py-1 z-10">{cat}</h4>
                        <table className="rom-table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th className="numeric w-24">Rate/day</th>
                                    <th className="numeric w-24">Qty</th>
                                    <th className="numeric w-24">Days</th>
                                    <th className="numeric w-32">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((it) => {
                                    const existing = draftByItem.get(it.item);
                                    const qty = existing?.qty ?? 0;
                                    const days = existing?.days ?? 0;
                                    const sub = qty * days * it.rate_per_day;
                                    return (
                                        <tr key={it.item} className={qty > 0 || days > 0 ? "bg-[var(--rom-orange-soft)]/30" : ""} data-testid={`eq-row-${it.item}`}>
                                            <td className="font-medium">{it.item}</td>
                                            <td className="numeric font-mono">{formatCurrencyExact(it.rate_per_day)}</td>
                                            <td className="numeric"><NumCell value={qty} onChange={(v) => upsert(it.item, it.rate_per_day, "qty", v)} testId={`eq-qty-${it.item}`} /></td>
                                            <td className="numeric"><NumCell step="0.5" value={days} onChange={(v) => upsert(it.item, it.rate_per_day, "days", v)} testId={`eq-days-${it.item}`} /></td>
                                            <td className="numeric font-mono font-semibold">{formatCurrencyExact(sub)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </Modal>
    );
}

/* ---------------- D: Subcontractors Modal ---------------- */
export function SubcontractorsModal({ open, onClose, lines, onSave }) {
    const [draft, setDraft] = useState(lines);
    useEffect(() => { if (open) setDraft(lines || []); }, [open, lines]);

    const setRow = (i, key, val) => setDraft((d) => {
        const next = [...d];
        next[i] = { ...next[i], [key]: val };
        return next;
    });
    const addRow = () => setDraft((d) => [...d, { item: "", qty: 1, dailyRate: 0, days: 1, markupPct: 0.20 }]);
    const delRow = (i) => setDraft((d) => d.filter((_, idx) => idx !== i));

    const total = draft.reduce((s, l) => s + subLineSubtotal(l), 0);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Schedule D — Subcontractors"
            subtitle="Markup default 20% per line (editable). e.g. fire watch, security, crane, scaffold."
            wide
            footer={
                <>
                    <button onClick={addRow} className="btn btn-ghost" data-testid="sub-add-row">
                        <Plus size={14} weight="bold" /> Add subcontractor
                    </button>
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-[var(--rom-text-2)]">
                            Sub-totals: <span className="font-mono font-bold text-[var(--rom-navy)]" data-testid="sub-modal-subtotal">{formatCurrencyExact(total)}</span>
                        </div>
                        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                        <button onClick={() => { onSave(draft); onClose(); }} className="btn btn-primary" data-testid="sub-modal-save">Save Subcontractors</button>
                    </div>
                </>
            }
        >
            {draft.length === 0 ? (
                <div className="text-center py-10 text-[var(--rom-text-3)] text-sm">
                    No subcontractors yet. Click <b>+ Add subcontractor</b> below.
                </div>
            ) : (
                <table className="rom-table">
                    <thead>
                        <tr>
                            <th>Item / Service</th>
                            <th className="numeric w-20">QTY</th>
                            <th className="numeric w-32">Daily Rate</th>
                            <th className="numeric w-20">Days</th>
                            <th className="numeric w-24">Markup</th>
                            <th className="numeric w-32">Subtotal</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {draft.map((row, i) => (
                            <tr key={i} data-testid={`sub-row-${i}`}>
                                <td>
                                    <input
                                        className="w-full bg-transparent border border-transparent hover:border-[var(--rom-border)] focus:border-[var(--rom-orange)] focus:outline-none rounded px-2 py-1"
                                        value={row.item || ""}
                                        placeholder="e.g. Fire watch"
                                        onChange={(e) => setRow(i, "item", e.target.value)}
                                        data-testid={`sub-item-${i}`}
                                    />
                                </td>
                                <td className="numeric"><NumCell value={row.qty} onChange={(v) => setRow(i, "qty", v)} testId={`sub-qty-${i}`} /></td>
                                <td className="numeric"><NumCell step="0.01" value={row.dailyRate} onChange={(v) => setRow(i, "dailyRate", v)} testId={`sub-rate-${i}`} /></td>
                                <td className="numeric"><NumCell value={row.days} onChange={(v) => setRow(i, "days", v)} testId={`sub-days-${i}`} /></td>
                                <td className="numeric">
                                    <div className="flex items-center gap-1">
                                        <NumCell step="0.01" value={(Number(row.markupPct ?? 0.20) * 100)} onChange={(v) => setRow(i, "markupPct", Number(v) / 100)} testId={`sub-markup-${i}`} />
                                        <span className="text-xs text-[var(--rom-text-3)]">%</span>
                                    </div>
                                </td>
                                <td className="numeric font-mono font-semibold">{formatCurrencyExact(subLineSubtotal(row))}</td>
                                <td className="text-right">
                                    <button onClick={() => delRow(i)} className="text-[var(--rom-danger)] hover:opacity-70 p-1" data-testid={`sub-del-${i}`}>
                                        <Trash size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Modal>
    );
}

/* ---------------- E: Miscellaneous Modal ---------------- */
export function MiscModal({ open, onClose, lines, onSave }) {
    const [draft, setDraft] = useState(lines);
    useEffect(() => { if (open) setDraft(lines || []); }, [open, lines]);

    const setRow = (i, key, val) => setDraft((d) => {
        const next = [...d];
        next[i] = { ...next[i], [key]: val };
        return next;
    });

    const total = draft.reduce((s, l) => s + miscLineSubtotal(l), 0);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Schedule E — Miscellaneous"
            subtitle="Travel, lodging, per diem, fuel, etc. All lines include 10+10% markup (20% total)."
            wide
            footer={
                <>
                    <span />
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-[var(--rom-text-2)]">
                            Misc. subtotal: <span className="font-mono font-bold text-[var(--rom-navy)]" data-testid="misc-modal-subtotal">{formatCurrencyExact(total)}</span>
                        </div>
                        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                        <button onClick={() => { onSave(draft); onClose(); }} className="btn btn-primary" data-testid="misc-modal-save">Save Miscellaneous</button>
                    </div>
                </>
            }
        >
            <table className="rom-table">
                <thead>
                    <tr>
                        <th>Line item</th>
                        <th className="numeric w-32">QTY ({/* unit varies */}…)</th>
                        <th className="numeric w-32">Unit rate</th>
                        <th className="numeric w-24">Days</th>
                        <th className="numeric w-32">Subtotal (+20%)</th>
                    </tr>
                </thead>
                <tbody>
                    {draft.map((row, i) => (
                        <tr key={i} data-testid={`misc-row-${i}`}>
                            <td className="font-medium">
                                {row.kind}
                                <div className="text-[10px] text-[var(--rom-text-3)] uppercase tracking-wider">{row.unitLabel}</div>
                            </td>
                            <td className="numeric"><NumCell value={row.qty} onChange={(v) => setRow(i, "qty", v)} testId={`misc-qty-${i}`} /></td>
                            <td className="numeric"><NumCell step="0.01" value={row.rate} onChange={(v) => setRow(i, "rate", v)} testId={`misc-rate-${i}`} /></td>
                            <td className="numeric">
                                {row.days != null ? (
                                    <NumCell value={row.days} onChange={(v) => setRow(i, "days", v)} testId={`misc-days-${i}`} />
                                ) : <span className="text-[var(--rom-text-3)]">—</span>}
                            </td>
                            <td className="numeric font-mono font-semibold">{formatCurrencyExact(miscLineSubtotal(row))}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Modal>
    );
}

/* ---------------- Labour Required (Staffing Summary) Modal ---------------- */
export function StaffingModal({ open, onClose, lines }) {
    const s = useMemo(() => staffingSummary(lines || []), [lines]);
    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Labour required for the job"
            subtitle="Staffing plan derived from Schedule A entries — mirrors the SERVPRO TM Calculator layout."
            wide
            footer={
                <>
                    <span />
                    <button onClick={onClose} className="btn btn-primary" data-testid="staffing-close">Close</button>
                </>
            }
        >
            <div className="grid grid-cols-4 gap-3 mb-5">
                <StaffStat label="Crew size" value={`${s.crew} people`} />
                <StaffStat label="Project days" value={`${s.days} d`} />
                <StaffStat label="Total hours" value={`${s.totalHours.toFixed(1)} h`} />
                <StaffStat label="OT hours" value={`${s.otHours.toFixed(1)} h`} highlight />
            </div>
            {s.roles.length === 0 ? (
                <div className="text-center py-8 text-[var(--rom-text-3)]">
                    No labour entered yet. Open Schedule A to add roles.
                </div>
            ) : (
                <table className="rom-table">
                    <thead>
                        <tr>
                            <th>Role</th>
                            <th className="numeric">QTY</th>
                            <th className="numeric">Reg Hrs/day</th>
                            <th className="numeric">OT Hrs/day</th>
                            <th className="numeric">Days</th>
                            <th className="numeric">Billing $/hr</th>
                            <th className="numeric">OT $/hr</th>
                            <th className="numeric">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {s.roles.map((l, i) => (
                            <tr key={i}>
                                <td className="font-medium">{l.role}</td>
                                <td className="numeric">{l.qty}</td>
                                <td className="numeric">{l.hours}</td>
                                <td className="numeric">{l.otHours || "—"}</td>
                                <td className="numeric">{l.days}</td>
                                <td className="numeric font-mono">{formatCurrencyExact(l.billingRate)}</td>
                                <td className="numeric font-mono">{formatCurrencyExact(l.otRate)}</td>
                                <td className="numeric font-mono font-semibold">{formatCurrencyExact(labourLineSubtotal(l))}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={7} className="text-right font-semibold">A: Labour total</td>
                            <td className="numeric font-display font-black">{formatCurrencyExact(s.roles.reduce((t, l) => t + labourLineSubtotal(l), 0))}</td>
                        </tr>
                    </tfoot>
                </table>
            )}
        </Modal>
    );
}

function StaffStat({ label, value, highlight }) {
    return (
        <div className={`p-3 rounded-[4px] border ${highlight ? "border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]" : "border-[var(--rom-border)] bg-slate-50"}`}>
            <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-3)]">{label}</div>
            <div className="font-display font-black text-2xl mt-1">{value}</div>
        </div>
    );
}

// Tiny number coercion (kept local; mirrors tmEngine.n)
const n = (x) => (Number(x) || 0);
